import { Component } from '@theme/component';

const SIZE_REGEX = /(\d+(?:[.,]\d+)?)\s*[×xX*]\s*(\d+(?:[.,]\d+)?)/;

/**
 * @typedef {object} ParsedVariant
 * @property {string} id
 * @property {string} title
 * @property {string[]} options
 * @property {boolean} available
 * @property {number} price
 * @property {number | null} compareAtPrice
 * @property {string} priceFormatted
 * @property {string} compareAtPriceFormatted
 * @property {number | null} width
 * @property {number | null} height
 * @property {number | null} area
 * @property {string | null} sizeValue
 */

/**
 * @typedef {object} OptionMeta
 * @property {string} name
 * @property {number} position
 * @property {string[]} values
 */

/**
 * @typedef {object} Threshold
 * @property {string} sizeValue   The exact size option value (e.g. "10X20CM").
 * @property {number} maxArea     The upper bound (inclusive) of customer area that maps to this preset.
 */

/**
 * @typedef {object} CustomSizePickerRefs
 * @property {HTMLInputElement} [tabPresetInput]
 * @property {HTMLInputElement} [tabCustomInput]
 * @property {HTMLInputElement} [widthInput]
 * @property {HTMLInputElement} [heightInput]
 * @property {HTMLElement} [matchedRow]
 * @property {HTMLElement} [matchedSize]
 * @property {HTMLElement} [matchedPrice]
 * @property {HTMLElement} [errorMessage]
 * @property {HTMLInputElement} propertyInput
 */

/**
 * @param {string} title
 * @returns {{ width: number, height: number } | null}
 */
function parseSize(title) {
  if (!title) return null;
  const match = title.match(SIZE_REGEX);
  if (!match) return null;
  const w = parseFloat((match[1] ?? '').replace(',', '.'));
  const h = parseFloat((match[2] ?? '').replace(',', '.'));
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
  return { width: w, height: h };
}

/**
 * Parse the merchant-provided area-thresholds textarea.
 * Expected per-line format: `<size_option_value> = <max_area_cm2>`.
 * Lines that don't parse are skipped.
 *
 * @param {string} raw
 * @returns {Threshold[]}
 */
function parseAreaThresholds(raw) {
  if (!raw || typeof raw !== 'string') return [];
  /** @type {Threshold[]} */
  const out = [];
  const lines = raw.split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const eqIdx = trimmed.lastIndexOf('=');
    if (eqIdx <= 0) continue;
    const sizeValue = trimmed.slice(0, eqIdx).trim();
    const areaStr = trimmed.slice(eqIdx + 1).trim().replace(',', '.');
    const maxArea = parseFloat(areaStr);
    if (!sizeValue || !Number.isFinite(maxArea) || maxArea <= 0) continue;
    out.push({ sizeValue, maxArea });
  }
  out.sort((a, b) => a.maxArea - b.maxArea);
  return out;
}

/**
 * Custom size picker block.
 * Renders every product option as a vertical button group; the option that
 * the merchant marked as the "size" option additionally gets a Preset/Custom
 * tab UI. Custom mode finds the best matching variant within the currently
 * selected non-size options using the configured pricing strategy.
 *
 * @extends Component<CustomSizePickerRefs>
 */
class CustomSizePicker extends Component {
  /** @type {ParsedVariant[]} */
  #variants = [];

  /** @type {OptionMeta[]} */
  #options = [];

  /** @type {string[]} */
  #selectedOptions = [];

  /** @type {number} */
  #sizeOptionPosition = -1;

  /** @type {string} */
  #pricingStrategy = 'closest_area';

  /** @type {Threshold[]} */
  #areaThresholds = [];

  /** @type {string | null} */
  #defaultVariantId = null;

  /** @type {string | null} */
  #lastSizeValue = null;

  /** @type {number} */
  #minDim = 1;

  /** @type {number} */
  #maxDim = 60;

  /** @type {string} */
  #productFormId = '';

  /** @type {HTMLElement | null} */
  #root = null;

  connectedCallback() {
    super.connectedCallback();

    this.#root = /** @type {HTMLElement | null} */ (this.querySelector('.csp-root'));
    const data = this.#root?.dataset ?? this.dataset;

    this.#minDim = parseFloat(data.minDim || '1');
    this.#maxDim = parseFloat(data.maxDim || '60');
    this.#productFormId = data.productFormId || '';
    this.#defaultVariantId = data.defaultVariantId || null;

    this.#loadPayload();
    this.#updateOptionAvailability();

    if (this.#sizeOptionPosition >= 0) {
      this.#lastSizeValue = this.#selectedOptions[this.#sizeOptionPosition] ?? null;
    }

    if (this.#variants.length === 0 || this.#sizeOptionPosition < 0) {
      const customInput = this.refs.tabCustomInput;
      if (customInput) {
        customInput.disabled = true;
        const label = this.querySelector(`label[for="${customInput.id}"]`);
        if (label instanceof HTMLElement) label.hidden = true;
      }
    }
  }

  #loadPayload() {
    const dataScript = this.querySelector('script[data-csp-payload]');
    if (!(dataScript instanceof HTMLScriptElement)) return;
    /** @type {any} */
    let payload;
    try {
      payload = JSON.parse(dataScript.textContent || '{}');
    } catch (error) {
      console.error('[custom-size-picker] failed to parse payload', error);
      return;
    }

    if (Array.isArray(payload?.options)) {
      this.#options = payload.options.map((entry) => ({
        name: String(entry.name ?? ''),
        position: Number(entry.position ?? 0),
        values: Array.isArray(entry.values) ? entry.values.map(String) : [],
      }));
    }

    this.#sizeOptionPosition = Number.isFinite(payload?.size_option_position)
      ? Number(payload.size_option_position)
      : -1;

    this.#pricingStrategy = String(payload?.pricing_strategy ?? 'closest_area');
    this.#areaThresholds = parseAreaThresholds(String(payload?.area_thresholds ?? ''));

    if (Array.isArray(payload?.selected_options)) {
      this.#selectedOptions = payload.selected_options.map(String);
    } else {
      this.#selectedOptions = this.#options.map(() => '');
    }

    if (Array.isArray(payload?.variants)) {
      this.#variants = payload.variants.map((entry) => {
        const optsArr = Array.isArray(entry.options) ? entry.options.map(String) : [];
        const sizeValue =
          this.#sizeOptionPosition >= 0 && this.#sizeOptionPosition < optsArr.length
            ? optsArr[this.#sizeOptionPosition]
            : null;
        const parsed = sizeValue ? parseSize(sizeValue) : null;
        const compareAt =
          entry.compare_at_price != null && entry.compare_at_price !== ''
            ? Number(entry.compare_at_price)
            : null;
        return {
          id: String(entry.id ?? ''),
          title: String(entry.title ?? ''),
          options: optsArr,
          available: Boolean(entry.available),
          price: Number(entry.price ?? 0),
          compareAtPrice: Number.isFinite(compareAt) ? compareAt : null,
          priceFormatted: String(entry.price_formatted ?? ''),
          compareAtPriceFormatted: String(entry.compare_at_price_formatted ?? ''),
          width: parsed?.width ?? null,
          height: parsed?.height ?? null,
          area: parsed ? parsed.width * parsed.height : null,
          sizeValue,
        };
      });
    }
  }

  /**
   * @param {Event} event
   */
  handleOptionClick(event) {
    const target = event.target;
    const button = target instanceof Element ? target.closest('.csp-option-button') : null;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.getAttribute('aria-disabled') === 'true') return;

    const positionAttr = button.dataset.optionPosition;
    const value = button.dataset.optionValue;
    if (positionAttr == null || value == null) return;
    const position = Number(positionAttr);
    if (!Number.isFinite(position)) return;

    this.#selectedOptions[position] = value;

    if (position === this.#sizeOptionPosition) {
      this.#lastSizeValue = value;
      this.#syncPropertyInput('');
      this.#hideMatched();
      this.#hideError();
    }

    this.#highlightOptionGroup(position, value);
    this.#updateOptionAvailability();
    this.#applySelection({ allowSizeFallback: position !== this.#sizeOptionPosition });
  }

  handleTabChange() {
    const isCustom = this.refs.tabCustomInput?.checked === true;
    const presetPanel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-panel="preset"]')
    );
    const customPanel = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-panel="custom"]')
    );

    if (presetPanel) presetPanel.hidden = isCustom;
    if (customPanel) customPanel.hidden = !isCustom;

    if (isCustom) {
      this.#evaluateCustom();
    } else {
      this.#syncPropertyInput('');
      this.#hideMatched();
      this.#hideError();
      if (this.#sizeOptionPosition >= 0 && this.#lastSizeValue) {
        this.#selectedOptions[this.#sizeOptionPosition] = this.#lastSizeValue;
        this.#highlightOptionGroup(this.#sizeOptionPosition, this.#lastSizeValue);
      }
      this.#updateOptionAvailability();
      this.#applySelection({ allowSizeFallback: true });
    }
  }

  handleDimensionInput() {
    this.#evaluateCustom();
  }

  /**
   * Evaluate the custom width/height input and resolve to a candidate variant
   * within the currently selected non-size options using the configured
   * pricing strategy.
   */
  #evaluateCustom() {
    if (this.#sizeOptionPosition < 0) return;

    const w = this.#readDim(this.refs.widthInput);
    const h = this.#readDim(this.refs.heightInput);

    if (w === null || h === null) {
      this.#hideMatched();
      this.#hideError();
      this.#updateAddToCartAvailability(false);
      this.#syncPropertyInput('');
      return;
    }

    if (w < this.#minDim || h < this.#minDim || w > this.#maxDim || h > this.#maxDim) {
      this.#hideMatched();
      this.#showError(this.#root?.dataset.errorOutOfRange || `${this.#minDim}–${this.#maxDim} cm`);
      this.#markInvalid(true);
      this.#updateAddToCartAvailability(false);
      this.#syncPropertyInput('');
      return;
    }

    this.#markInvalid(false);
    this.#hideError();

    const candidates = this.#candidatesForSelection();
    const matched = this.#matchByStrategy(candidates, w * h);
    if (!matched) {
      this.#hideMatched();
      this.#updateAddToCartAvailability(false);
      this.#syncPropertyInput('');
      return;
    }

    this.#showMatched(matched);
    this.#applyVariantId(matched.id);
    this.#updatePagePrice(matched.id);
    if (this.#sizeOptionPosition >= 0) {
      this.#selectedOptions[this.#sizeOptionPosition] = matched.sizeValue ?? '';
      this.#updateOptionGroupHeader(this.#sizeOptionPosition, this.#formatPropertyValue(w, h));
    }

    if (!matched.available) {
      this.#showError(this.#root?.dataset.errorSoldOut || 'Sold out');
      this.#updateAddToCartAvailability(false);
      this.#syncPropertyInput('');
      return;
    }

    this.#updateAddToCartAvailability(true);
    this.#syncPropertyInput(this.#formatPropertyValue(w, h));
  }

  /**
   * Apply the current selectedOptions to the form. When `allowSizeFallback`
   * is true and the chosen non-size selection has no matching size, we keep
   * the picker in a sane state (mark size buttons disabled, etc.).
   *
   * @param {{ allowSizeFallback?: boolean }} [opts]
   */
  #applySelection(opts = {}) {
    const isCustomMode = this.refs.tabCustomInput?.checked === true;
    if (isCustomMode) {
      this.#evaluateCustom();
      return;
    }

    const variant = this.#findVariantByOptions(this.#selectedOptions);
    if (variant) {
      this.#applyVariantId(variant.id);
      this.#updatePagePrice(variant.id);
      this.#updateAddToCartAvailability(variant.available);
      this.#syncPropertyInput('');
      this.#hideError();
      if (this.#sizeOptionPosition >= 0 && variant.sizeValue) {
        this.#updateOptionGroupHeader(this.#sizeOptionPosition, variant.sizeValue);
      }
      if (!variant.available) {
        this.#showError(this.#root?.dataset.errorSoldOut || 'Sold out');
      }
      return;
    }

    if (opts.allowSizeFallback && this.#sizeOptionPosition >= 0) {
      const candidates = this.#candidatesForSelection();
      const fallback = candidates.find((v) => v.available) || candidates[0] || null;
      if (fallback) {
        this.#selectedOptions[this.#sizeOptionPosition] = fallback.sizeValue ?? '';
        this.#lastSizeValue = fallback.sizeValue ?? this.#lastSizeValue;
        this.#highlightOptionGroup(this.#sizeOptionPosition, fallback.sizeValue ?? '');
        this.#updateOptionGroupHeader(this.#sizeOptionPosition, fallback.sizeValue ?? '');
        this.#applyVariantId(fallback.id);
        this.#updatePagePrice(fallback.id);
        this.#updateAddToCartAvailability(fallback.available);
        this.#syncPropertyInput('');
        this.#hideError();
        if (!fallback.available) {
          this.#showError(this.#root?.dataset.errorSoldOut || 'Sold out');
        }
        return;
      }
    }

    this.#updateAddToCartAvailability(false);
    this.#showError(this.#root?.dataset.errorUnavailable || '');
  }

  /**
   * @returns {ParsedVariant[]}
   */
  #candidatesForSelection() {
    return this.#variants.filter((v) => {
      for (let i = 0; i < this.#options.length; i++) {
        if (i === this.#sizeOptionPosition) continue;
        const selected = this.#selectedOptions[i];
        if (!selected) continue;
        if (v.options[i] !== selected) return false;
      }
      return true;
    });
  }

  /**
   * @param {string[]} selection
   * @returns {ParsedVariant | null}
   */
  #findVariantByOptions(selection) {
    return (
      this.#variants.find((v) => {
        for (let i = 0; i < this.#options.length; i++) {
          const sel = selection[i];
          if (!sel) return false;
          if (v.options[i] !== sel) return false;
        }
        return true;
      }) ?? null
    );
  }

  /**
   * Pick the best variant from the candidate list for the given target area
   * using the merchant-configured pricing strategy.
   *
   * @param {ParsedVariant[]} candidates
   * @param {number} targetArea
   * @returns {ParsedVariant | null}
   */
  #matchByStrategy(candidates, targetArea) {
    const sized = candidates.filter((v) => v.area != null);
    if (sized.length === 0) return null;

    if (this.#pricingStrategy === 'area_thresholds' && this.#areaThresholds.length > 0) {
      for (const threshold of this.#areaThresholds) {
        if (targetArea > threshold.maxArea) continue;
        const match = sized.find((v) => v.sizeValue === threshold.sizeValue);
        if (match) return match;
      }
      const sortedByArea = [...sized].sort(
        (a, b) => /** @type {number} */ (b.area) - /** @type {number} */ (a.area)
      );
      return sortedByArea[0] ?? null;
    }

    if (this.#pricingStrategy === 'round_up') {
      const upper = sized
        .filter((v) => /** @type {number} */ (v.area) >= targetArea)
        .sort((a, b) => /** @type {number} */ (a.area) - /** @type {number} */ (b.area));
      if (upper.length > 0) return upper[0];
      const largest = [...sized].sort(
        (a, b) => /** @type {number} */ (b.area) - /** @type {number} */ (a.area)
      );
      return largest[0] ?? null;
    }

    if (this.#pricingStrategy === 'round_down') {
      const lower = sized
        .filter((v) => /** @type {number} */ (v.area) <= targetArea)
        .sort((a, b) => /** @type {number} */ (b.area) - /** @type {number} */ (a.area));
      if (lower.length > 0) return lower[0];
      const smallest = [...sized].sort(
        (a, b) => /** @type {number} */ (a.area) - /** @type {number} */ (b.area)
      );
      return smallest[0] ?? null;
    }

    let best = null;
    let bestDiff = Number.POSITIVE_INFINITY;
    for (const variant of sized) {
      const diff = Math.abs(/** @type {number} */ (variant.area) - targetArea);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = variant;
      }
    }
    return best;
  }

  /**
   * Recompute aria-disabled / sold-out indicators for every option button
   * based on the current selectedOptions of OTHER positions.
   */
  #updateOptionAvailability() {
    for (let pos = 0; pos < this.#options.length; pos++) {
      const buttons = this.querySelectorAll(`.csp-option-button[data-option-position="${pos}"]`);
      buttons.forEach((button) => {
        if (!(button instanceof HTMLElement)) return;
        const value = button.dataset.optionValue ?? '';
        const hasAny = this.#variants.some((v) => {
          if (v.options[pos] !== value) return false;
          for (let i = 0; i < this.#options.length; i++) {
            if (i === pos) continue;
            const sel = this.#selectedOptions[i];
            if (!sel) continue;
            if (v.options[i] !== sel) return false;
          }
          return true;
        });
        const hasAvailable = this.#variants.some((v) => {
          if (!v.available) return false;
          if (v.options[pos] !== value) return false;
          for (let i = 0; i < this.#options.length; i++) {
            if (i === pos) continue;
            const sel = this.#selectedOptions[i];
            if (!sel) continue;
            if (v.options[i] !== sel) return false;
          }
          return true;
        });

        if (!hasAny) {
          button.setAttribute('aria-disabled', 'true');
        } else if (!hasAvailable) {
          button.setAttribute('aria-disabled', 'true');
        } else {
          button.removeAttribute('aria-disabled');
        }
      });
    }
  }

  /**
   * @param {HTMLInputElement | undefined} input
   * @returns {number | null}
   */
  #readDim(input) {
    if (!input) return null;
    const trimmed = input.value.trim();
    if (trimmed === '') return null;
    const value = parseFloat(trimmed.replace(',', '.'));
    if (!Number.isFinite(value) || value <= 0) return null;
    return value;
  }

  /**
   * @param {ParsedVariant} variant
   */
  #showMatched(variant) {
    if (this.#root?.dataset.showMatched !== 'true') return;
    const { matchedRow, matchedSize, matchedPrice } = this.refs;
    if (matchedSize) matchedSize.textContent = variant.sizeValue || variant.title;
    if (matchedPrice) matchedPrice.textContent = variant.priceFormatted;
    if (matchedRow) matchedRow.hidden = false;
  }

  #hideMatched() {
    const { matchedRow } = this.refs;
    if (matchedRow) matchedRow.hidden = true;
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.hidden = false;
  }

  #hideError() {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.hidden = true;
    errorMessage.textContent = '';
  }

  /**
   * @param {boolean} invalid
   */
  #markInvalid(invalid) {
    const { widthInput, heightInput } = this.refs;
    [widthInput, heightInput].forEach((input) => {
      if (!input) return;
      if (invalid) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
    });
  }

  /**
   * @param {number} position
   * @param {string} value
   */
  #highlightOptionGroup(position, value) {
    const buttons = this.querySelectorAll(
      `.csp-option-button[data-option-position="${position}"]`
    );
    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const isCurrent = button.dataset.optionValue === value;
      if (isCurrent) {
        button.dataset.current = 'true';
        button.setAttribute('aria-pressed', 'true');
      } else {
        delete button.dataset.current;
        button.setAttribute('aria-pressed', 'false');
      }
    });

    this.#updateOptionGroupHeader(position, value);
  }

  /**
   * @param {number} position
   * @param {string} value
   */
  #updateOptionGroupHeader(position, value) {
    const header = this.querySelector(
      `.csp-option-current[data-option-position="${position}"]`
    );
    if (header instanceof HTMLElement) {
      header.textContent = value;
    }
  }

  /**
   * @param {string} variantId
   */
  #applyVariantId(variantId) {
    const form = this.#getProductForm();
    if (!form) return;
    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variantId;
    }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      if (url.href !== window.location.href) {
        window.history.replaceState({}, '', url.toString());
      }
    } catch (_error) {
      // Non-blocking: URL update is best-effort.
    }
  }

  /**
   * @param {boolean} available
   */
  #updateAddToCartAvailability(available) {
    const form = this.#getProductForm();
    if (!form) return;
    const addToCart = form.querySelector('add-to-cart-component');
    if (!addToCart) return;
    const button = addToCart.querySelector('[ref="addToCartButton"], button[type="submit"]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (available) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
    } else {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }
  }

  /**
   * @param {string} value
   */
  #syncPropertyInput(value) {
    const { propertyInput } = this.refs;
    if (!propertyInput) return;
    propertyInput.value = value;
    propertyInput.disabled = value === '';
  }

  /**
   * @param {number} w
   * @param {number} h
   * @returns {string}
   */
  #formatPropertyValue(w, h) {
    const format = (value) =>
      Number.isInteger(value) ? value.toString() : value.toString().replace('.', ',');
    return `${format(w)} × ${format(h)} cm`;
  }

  /**
   * @param {string} variantId
   */
  #updatePagePrice(variantId) {
    const variant = this.#variants.find((v) => v.id === variantId);
    if (!variant) return;

    const productId = this.#root?.dataset.productId || '';
    const productPrices = document.querySelectorAll(
      `product-price[data-product-id="${productId}"]`
    );

    productPrices.forEach((priceComponent) => {
      const priceContainer = priceComponent.querySelector('[ref="priceContainer"]');
      if (!priceContainer) return;
      this.#renderPriceContainer(priceContainer, variant);
    });
  }

  /**
   * @param {Element} container
   * @param {ParsedVariant} variant
   */
  #renderPriceContainer(container, variant) {
    const showCompare = variant.compareAtPrice != null && variant.compareAtPrice > variant.price;
    const compareHtml = showCompare
      ? `<span role="group"><span class="visually-hidden">${this.#tComparePrice()}&nbsp;</span><span class="compare-at-price">${this.#escape(variant.compareAtPriceFormatted)}</span></span>`
      : '';
    const priceHtml = showCompare
      ? `<span role="group"><span class="visually-hidden">${this.#tSalePrice()}&nbsp;</span><span class="price">${this.#escape(variant.priceFormatted)}</span></span>`
      : `<span class="price">${this.#escape(variant.priceFormatted)}</span>`;

    container.innerHTML = `${compareHtml}${priceHtml}`;
  }

  #tComparePrice() {
    return (typeof Theme !== 'undefined' && Theme?.translations?.regularPrice) || 'Regular price';
  }

  #tSalePrice() {
    return (typeof Theme !== 'undefined' && Theme?.translations?.salePrice) || 'Sale price';
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #escape(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getProductForm() {
    if (!this.#productFormId) return null;
    const form = document.getElementById(this.#productFormId);
    return form instanceof HTMLFormElement ? form : null;
  }
}

if (!customElements.get('custom-size-picker')) {
  customElements.define('custom-size-picker', CustomSizePicker);
}
